import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Generic infinite-scroll accumulator on top of any RTK Query paged list hook.
 *
 * The server stays the source of truth for order, filtering, search and sorting:
 * we simply request page 1, 2, 3… of the SAME filter and append the results.
 *
 *  - No duplicates: incoming rows are de-duplicated by `id` before appending.
 *  - Correct order: pages are appended in sequence; within a page the server order is preserved.
 *  - Filter/search/sort changes reset the accumulator back to page 1 (synchronously, so the
 *    very first request already uses the new filter — no stale page is fetched or shown).
 *
 * @param useListQuery an RTK Query hook: (args) => ({ data: { items, totalCount, page }, isFetching })
 * @param filter       the filter object WITHOUT page/pageSize (search, sort, status, …)
 * @param options      { pageSize }
 * @returns { items, total, hasMore, isFetching, isLoading, sentinelRef, loadMore, reset }
 */
export function useInfiniteQuery(useListQuery, filter, { pageSize = 25 } = {}) {
  const filterKey = JSON.stringify(filter ?? {});
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);

  // Adjust state during render when the filter changes (React's "derived state" pattern):
  // resets synchronously so the next query call below already uses page 1 + the new filter.
  const keyRef = useRef(filterKey);
  if (keyRef.current !== filterKey) {
    keyRef.current = filterKey;
    setPage(1);
    setItems([]);
  }

  const { data, isFetching, isLoading } = useListQuery({ ...(filter ?? {}), page, pageSize });

  // Accumulate page results. `data.page` from the response decides replace (page 1) vs append.
  useEffect(() => {
    if (!data) return;
    const incoming = data.items ?? [];
    const respPage = data.page ?? 1;
    // Dedup by whichever stable id the row carries: `id` for most lists, `patientId` for the
    // patient-session report. Without this, id-less rows collapse to one and paging stalls.
    const keyOf = (x) => x?.id ?? x?.patientId ?? x?.invoiceId;
    setItems((prev) => {
      // Page-1 response while only one page is loaded: plain replace, so rows that no longer
      // match the filter (deleted / moved out) drop off. `pageSize` bounds "one page loaded".
      if (respPage <= 1 && prev.length <= Math.max(pageSize, incoming.length)) return incoming;
      // Re-fetch of an already-loaded page (RTK tag invalidation after a mutation): REPLACE the
      // stale copy of every row that came back in place, then append genuinely new rows. The old
      // "skip rows we've already seen" dedup kept the stale row and threw the fresh one away,
      // which is why status changes didn't show up until a manual reload.
      const freshByKey = new Map(incoming.filter(Boolean).map((x) => [keyOf(x), x]));
      const merged = prev.map((x) => freshByKey.get(keyOf(x)) ?? x);
      const seen = new Set(prev.map(keyOf));
      return [...merged, ...incoming.filter((x) => x && !seen.has(keyOf(x)))];
    });
  }, [data, pageSize]);

  const total = data?.totalCount ?? 0;
  const hasMore = items.length < total;

  const loadMore = useCallback(() => {
    if (!isFetching && items.length < (data?.totalCount ?? 0)) setPage((p) => p + 1);
  }, [isFetching, items.length, data]);

  const reset = useCallback(() => { setPage(1); setItems([]); }, []);

  // Surgically update one already-loaded row (e.g. after an inline edit) without a full reload.
  const patchItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }, []);

  // Drop one already-loaded row (e.g. it no longer matches the active view after a status change).
  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  // IntersectionObserver sentinel — auto-loads the next page as it scrolls into view.
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!hasMore || isFetching) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setPage((p) => p + 1); },
      { rootMargin: '250px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, isFetching]);

  return { items, total, hasMore, isFetching, isLoading, sentinelRef, loadMore, reset, patchItem, removeItem, page };
}
